import type { Diagnostics, ListenDiagnostic } from '@/diagnostic/domain/Diagnostics.ts';
import { type AuthenticatedFetcher } from '@/generic/authentication';
import { type ApiInformationToDiagnose } from '@/diagnostic/domain/ApiInformation.ts';
import { type DiagnosticId } from '@/diagnostic/domain/DiagnosticId.ts';
import type { DiagnosticSummary } from '@/diagnostic/domain/DiagnosticSummary.ts';
import type { Unsubscribe } from '@/diagnostic/domain/Unsubscribe.ts';
import { DiagnosticReport } from '@/diagnostic/domain/DiagnosticReport.ts';
import { DiagnosticHasFinished } from '@/diagnostic/domain/DiagnosticHasFinished.ts';
import { type Page, Pagination, paginationToQuery } from '@/generic/pagination';
import {
  parseJsonToDiagnosticsSummary,
  parseJsonToDiagnosticsSummaryPage,
} from '@/diagnostic/secondary/CleanApiDiagnosticsJson.ts';
import { parseJsonToDiagnosticId } from '@/diagnostic/secondary/CleanApiDiagnosticIdJson.ts';
import { parseJsonToDiagnosticInProgress } from '@/diagnostic/secondary/CleanApiDiagnosticJson.ts';
import { inclusionToPayload } from '@/diagnostic/secondary/InclusionPayload.ts';
import {
  type Diagnostic,
  DiagnosticCompleted,
  DiagnosticNotFollowed,
} from '@/diagnostic/domain/Diagnostic.ts';
import type { DiagnosticFollowUp } from '@/diagnostic/domain/DiagnosticFollowUp.ts';
import { Poll } from '@/generic/polling';

const DIAGNOSED_EVENT = 'cleanapi.diagnosed';
const DIAGNOSTIC_COMPLETED_EVENT = 'cleanapi.diagnostic.completed';
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

export class CleanApiDiagnosticsHttp implements Diagnostics {
  readonly #authenticatedFetcher: AuthenticatedFetcher;
  readonly #poll: Poll;
  readonly #consecutiveFailures: Map<string, number>;

  constructor(authenticatedFetcher: AuthenticatedFetcher, pollingDuration: number) {
    this.#authenticatedFetcher = authenticatedFetcher;
    this.#poll = Poll.of(pollingDuration);
    this.#consecutiveFailures = new Map();
    this.#poll.run();
  }

  async diagnose(api: ApiInformationToDiagnose): Promise<DiagnosticId> {
    const body = new FormData();
    body.append('file', api.content);
    body.append('inclusion', inclusionToPayload(api.rules.inclusion));
    for (const ruleId of api.rules.list) {
      body.append('ruleIds', ruleId.value);
    }

    return await this.#authenticatedFetcher
      .fetch('/api/diagnostics', {
        method: 'POST',
        body,
      })
      .then(parseJsonToDiagnosticId);
  }

  private async progress(id: DiagnosticId): Promise<Diagnostic> {
    const response = await this.#authenticatedFetcher.raw(`/api/diagnostics/${id.value}`, {
      method: 'GET',
      retry: false,
    });
    if (response.status === 202) {
      return parseJsonToDiagnosticInProgress(response._data);
    }
    return DiagnosticCompleted.of(await this.getReport(id));
  }

  listen(id: DiagnosticId): ListenDiagnostic {
    const diagnosedEventName = `${DIAGNOSED_EVENT}.${id.value}`;

    this.follow(id, diagnosedEventName);

    return (diagnosed) => {
      const listenEvent = (event: Event) => diagnosed((event as CustomEvent).detail);
      globalThis.addEventListener(diagnosedEventName, listenEvent);

      return this.followUp(id, diagnosedEventName, listenEvent);
    };
  }

  private followUp(
    id: DiagnosticId,
    diagnosedEventName: string,
    listenEvent: (event: Event) => void,
  ): DiagnosticFollowUp {
    const stop = () => {
      this.stopFollowing(id);
      globalThis.removeEventListener(diagnosedEventName, listenEvent);
    };
    return {
      retry: () => this.follow(id, diagnosedEventName),
      stop,
      cancel: () => {
        stop();
        return this.cancel(id);
      },
    };
  }

  private follow(id: DiagnosticId, diagnosedEventName: string): void {
    this.#consecutiveFailures.set(id.value, 0);
    this.#poll.set(id.value, () =>
      this.progress(id).then(
        (diagnostic) => this.onProgress(id, diagnosedEventName, diagnostic),
        (error: unknown) => this.onProgressFailure(id, diagnosedEventName, error),
      ),
    );
  }

  private onProgress(id: DiagnosticId, diagnosedEventName: string, diagnostic: Diagnostic): void {
    this.#consecutiveFailures.set(id.value, 0);
    globalThis.dispatchEvent(new CustomEvent(diagnosedEventName, { detail: diagnostic }));

    if (diagnostic instanceof DiagnosticCompleted) {
      this.stopFollowing(id);
    }
  }

  private onProgressFailure(
    id: DiagnosticId,
    diagnosedEventName: string,
    error: unknown,
  ): void | never {
    const failures = (this.#consecutiveFailures.get(id.value) ?? 0) + 1;
    this.#consecutiveFailures.set(id.value, failures);

    if (failures < MAX_CONSECUTIVE_POLL_FAILURES) {
      return;
    }

    globalThis.dispatchEvent(
      new CustomEvent(diagnosedEventName, { detail: DiagnosticNotFollowed.of(error) }),
    );

    throw error;
  }

  private stopFollowing(id: DiagnosticId): void {
    this.#poll.remove(id.value);
    this.#consecutiveFailures.delete(id.value);
  }

  notifyDiagnosticCompleted(): void {
    globalThis.dispatchEvent(
      new CustomEvent(DIAGNOSTIC_COMPLETED_EVENT, { detail: DiagnosticHasFinished }),
    );
  }

  onDiagnosticCompleted(listener: (event: DiagnosticHasFinished) => void): Unsubscribe {
    const handler = (event: Event) => listener((event as CustomEvent).detail);
    globalThis.addEventListener(DIAGNOSTIC_COMPLETED_EVENT, handler);

    return () => {
      globalThis.removeEventListener(DIAGNOSTIC_COMPLETED_EVENT, handler);
    };
  }

  async getReport(id: DiagnosticId): Promise<DiagnosticReport> {
    const blob = await this.#authenticatedFetcher.fetch<Blob, 'blob'>(
      `/api/diagnostics/${id.value}/report`,
      { method: 'GET', responseType: 'blob' },
    );
    return DiagnosticReport.of(await blob.text());
  }

  async cancel(id: DiagnosticId): Promise<void> {
    await this.#authenticatedFetcher.fetch(`/api/diagnostics/${id.value}`, {
      method: 'DELETE',
    });
  }

  async history(size: number): Promise<DiagnosticSummary[]> {
    return await this.#authenticatedFetcher
      .fetch('/api/diagnostics', {
        method: 'GET',
        query: paginationToQuery(
          Pagination.of({
            page: 1,
            pageSize: size,
          }),
        ),
      })
      .then(parseJsonToDiagnosticsSummary);
  }

  async historyPage(pagination: Pagination): Promise<Page<DiagnosticSummary>> {
    return await this.#authenticatedFetcher
      .fetch('/api/diagnostics', {
        method: 'GET',
        query: paginationToQuery(pagination),
      })
      .then(parseJsonToDiagnosticsSummaryPage);
  }
}
